import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { color } from 'src/constants/color.css';
import { Model } from 'src/models';

import NewServiceOrderAction from './NewServiceOrderAction';
import ProjectModal from './ProjectModal';
import ProjectsAction from './ProjectsAction';
import ProjectsCompanyHeader from './ProjectsCompanyHeader';
import ProjectsRow from './ProjectsRow';
import ProjectsSectionHeading from './ProjectsSectionHeading';
import ProjectsServiceOrderRow from './ProjectsServiceOrderRow';

type Props = {
  summary: Api.CompanyProjectSummary.V;
  invalidate: () => unknown;
};

/**
 * One client's card: the client itself, its projects with the service orders
 * that belong to them, any service orders standing on their own, and the New
 * project row that the card is the place to start from.
 */
export default function ProjectsClientCard({ summary, invalidate }: Props) {
  const {
    company,
    numOpenTickets,
    maintenanceMinutesLogged,
    projects,
    serviceOrders,
  } = summary;

  const [creatingProject, setCreatingProject] = React.useState(false);
  const [editingProject, setEditingProject] = React.useState<{
    id: string;
    name: string;
  } | null>(null);

  return (
    <html.div style={styles.card}>
      <ProjectsCompanyHeader
        company={company}
        numOpenTickets={numOpenTickets}
        maintenanceMinutesLogged={maintenanceMinutesLogged}
      />

      {projects.length > 0 && (
        <>
          <html.span style={styles.rule} />
          <ProjectsSectionHeading label="Projects" count={projects.length} />
          {projects.map(({ project, serviceOrders: theirs }) => (
            <React.Fragment key={project['@id']}>
              <ProjectsRow
                target={Model.TimerTarget.fromProject(project, company)}
                onEdit={() =>
                  setEditingProject({ id: project.id, name: project.name })
                }
              />
              {theirs.map((serviceOrder) => (
                <ProjectsServiceOrderRow
                  key={serviceOrder['@id']}
                  serviceOrder={serviceOrder}
                  company={company}
                  nested
                />
              ))}
              <NewServiceOrderAction
                company={company}
                project={project}
                level="project"
              />
            </React.Fragment>
          ))}
        </>
      )}

      {serviceOrders.length > 0 && (
        <>
          <html.span style={styles.rule} />
          <ProjectsSectionHeading
            label="Service Orders"
            count={serviceOrders.length}
            hint="not tied to a project"
          />
          {serviceOrders.map((serviceOrder) => (
            <ProjectsServiceOrderRow
              key={serviceOrder['@id']}
              serviceOrder={serviceOrder}
              company={company}
            />
          ))}
          <NewServiceOrderAction company={company} level="card" />
        </>
      )}

      <html.span style={styles.rule} />
      <ProjectsAction
        label="New project"
        level="card"
        onClick={() => setCreatingProject(true)}
      />

      {creatingProject && (
        <ProjectModal
          mode="create"
          company={company}
          onClose={() => setCreatingProject(false)}
          invalidate={invalidate}
        />
      )}

      {editingProject && (
        <ProjectModal
          mode="edit"
          company={company}
          project={editingProject}
          onClose={() => setEditingProject(null)}
          invalidate={invalidate}
        />
      )}
    </html.div>
  );
}

const styles = css.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
    // Deliberately not clipped: the category menus open past the card.  The
    // header rounds its own top corners instead, and the padding below the
    // last row keeps a running row off the bottom ones.
  },
  rule: {
    height: 1,
    backgroundColor: color.neutral20,
  },
});
